import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Table } from "reactstrap";
import useUserQuery from "../../common/lib/fetch/useUserQuery";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import QueryView from "../../common/lib/QueryView";
import SpinnerButton from "../../common/SpinnerButton";
import { adminFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import useEnableDriver from "../../requests/useUnsuspendedDriver";
import useUpdateCarrierDriverSettings, { CarrierDriverSettings } from "../../requests/useUpdateCarrierDriverSettings";
import { useUser } from "../../userAccess/useUser";

interface SuspendedDriver {
  id: string,
  question: string,
  answer: string,
  driverEmail?: string,
  driverNumber?: string,
}

interface CarrierDriverPageSettings extends CarrierDriverSettings {
  suspendedDrivers: SuspendedDriver[]
}

const CarrierDriverInfo = () => {
  const displayMessage = useDisplayMessage();
  const user = useUser();

  const useGetSiteSettings = useUserQuery<CarrierDriverPageSettings>({
    path: `/api/sites/${user.selectedSite!.id}/settings`
  }, {
    onError: (err) => displayMessage.fail(adminFacingErrorMessage(err))
  });

  const driverEnabled = (dd: SuspendedDriver) => {
    useGetSiteSettings.query();
    displayMessage.success(`Driver ${dd?.driverEmail ?? dd?.driverNumber} successfully unsuspended`);
  }

  return <QueryView
    query={useGetSiteSettings}
    renderData={carrierDriverSettings => <>
      {displayMessage?.message != null && <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />}
      <CarrierDriverInfoForm initialSettings={carrierDriverSettings} driverEnabled={driverEnabled} />
    </>
    } />
}

type CarrierDriverInfoFormProps = { initialSettings: CarrierDriverPageSettings, driverEnabled: (dd: SuspendedDriver) => void }

const CarrierDriverInfoForm = ({ initialSettings, driverEnabled }: CarrierDriverInfoFormProps) => {
  const displayMessage = useDisplayMessage();
  const [driverToEnable, setDriverToEnable] = useState<SuspendedDriver | undefined>(undefined);

  const updateSiteSettings = useUpdateCarrierDriverSettings({
    onSuccess: () => displayMessage.success("Carrier Driver settings updated"),
    onError: (err) => displayMessage.fail(adminFacingErrorMessage(err))
  });

  const unsuspendDriver = useEnableDriver(driverToEnable?.id ?? '', {
    onSuccess: () => {
      driverEnabled(driverToEnable!);
    },
    onError: (err) => {
      displayMessage.fail(adminFacingErrorMessage(err));
    },
    onComplete: () => {
      setDriverToEnable(undefined);
    }
  });

  const { register, handleSubmit, watch, setValue } = useForm<CarrierDriverSettings>({
    defaultValues: {
      requireCarrierNumber: initialSettings.requireCarrierNumber,
      requireTransportNumber: initialSettings.requireTransportNumber,
      requireDriverNumber: initialSettings.requireDriverNumber,
      requireDriverPassword: initialSettings.requireDriverPassword,
    }
  });

  const onEnableDriver = (driver: SuspendedDriver) => {
    setDriverToEnable(driver);
    unsuspendDriver.request();
  }

  const onSubmit: SubmitHandler<CarrierDriverSettings> = (data) => updateSiteSettings.request(data);

  const requireDriverNumber = watch("requireDriverNumber");
  if (!requireDriverNumber) { setValue("requireDriverPassword", false) }

  return <>
    {displayMessage?.message != null && <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />}
    <form data-testid="carrierDriverSettings-form" className="mx-auto w-50 mt-3">
      <h2>Carrier/Driver Info</h2>
      <div className="border border-dark rounded">
        <div className="m-3">
          <div className="form-check">
            <input type="checkbox" id="RequireCarrierNumber" className="form-check-input" {...register("requireCarrierNumber")} />
            <label className="form-check-label" htmlFor="RequireCarrierNumber">Prompt for Carrier Number</label>
          </div>
          <div className="form-check">
            <input type="checkbox" id="RequireTransportNumber" className="form-check-input" {...register("requireTransportNumber")} />
            <label className="form-check-label" htmlFor="RequireTransportNumber">Prompt for Transport Number</label>
          </div>
          <div className="form-check">
            <input type="checkbox" id="RequireDriverNumber" className="form-check-input" {...register("requireDriverNumber")} />
            <label className="form-check-label" htmlFor="RequireDriverNumber">Prompt for Driver Number</label>
          </div>
          <div className="form-check">
            <input type="checkbox" id="RequireDriverPassword" className="form-check-input" disabled={!requireDriverNumber} {...register("requireDriverPassword")} />
            <label className="form-check-label" htmlFor="RequireDriverPassword">Prompt for Driver Password</label>
          </div>
        </div>
      </div>
    </form>
    <div className="align-self-end me-8">
      <SpinnerButton
        className="btn btn-primary mx-4 mb-4 px-5 py-3"
        spinning={updateSiteSettings.isLoading}
        onClick={handleSubmit(onSubmit)}>
        Save
      </SpinnerButton>
    </div>
    <hr className="text-dark mx-0 my-5" />
    <div>
      <h2>Suspended Drivers</h2>
      {
        initialSettings.suspendedDrivers.length === 0
          ? <h4 className="mt-4">No drivers are currently suspended</h4>
          : <div>
            <Table className="table-bordered">
              <thead>
                <tr>
                  <th>
                    Driver Email
                  </th>
                  <th>
                    Driver Number
                  </th>
                  <th>
                    Question
                  </th>
                  <th>
                    Answer
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {
                  initialSettings.suspendedDrivers.map(sd =>
                    <tr key={sd.id}>
                      <td>
                        {sd.driverEmail}
                      </td>
                      <td>
                        {sd.driverNumber}
                      </td>
                      <td>
                        {sd.question}
                      </td>
                      <td>
                        {sd.answer}
                      </td>
                      <td>
                        <SpinnerButton
                          onClick={() => onEnableDriver(sd)}
                          spinning={unsuspendDriver.isLoading}>
                          Unsuspend
                        </SpinnerButton>
                      </td>
                    </tr>
                  )
                }
              </tbody>
            </Table>
          </div>
      }
    </div>
  </>
}

export { CarrierDriverInfo };
